import {
  CheckAgeSignalsResult,
  UserStatus,
} from 'insight-age-check';

export enum AgeSignalSource {
  Real = 'REAL',
  Simulated = 'SIMULATED',
}

export enum SimulatedAgeSignalScenario {
  VerifiedAdult = 'VERIFIED_ADULT',
  DeclaredTeen = 'DECLARED_TEEN',
  SupervisedTeen = 'SUPERVISED_TEEN',
  SupervisedApprovalPending = 'SUPERVISED_APPROVAL_PENDING',
  SupervisedApprovalDenied = 'SUPERVISED_APPROVAL_DENIED',
  Unknown = 'UNKNOWN',
  DeclinedSharing = 'DECLINED_SHARING',
  NoSignal = 'NO_SIGNAL',
}

export interface AgeSignalDecision {
  allowed: boolean;
  message?: string;
}

export interface AgeGateReviewResult {
  allowed: boolean;
  message: string;
  debugData: unknown;
}

export const defaultAgeGates = [13, 15, 18];

export const getSignificantUpdateApprovalPendingMessage = (appName: string) => (
  `${appName} will be available after a parent or guardian approves this update.`
);

export const getSignificantUpdateApprovalDeniedMessage = (appName: string) => (
  `${appName} isn't available because this update has not been approved by a parent or guardian.`
);

export const getAgeInformationUnavailableMessage = (appName: string) => (
  `${appName} isn't available right now because age assurance information for this update could not be confirmed on this device.`
);

export const getAdultAcknowledgmentUnavailableMessage = (appName: string) => (
  `${appName} isn't available right now because this update could not be acknowledged on this device.`
);

export const simulatedAgeSignalOptions = [
  {
    value: SimulatedAgeSignalScenario.VerifiedAdult,
    label: 'Verified adult',
  },
  {
    value: SimulatedAgeSignalScenario.DeclaredTeen,
    label: 'Declared teen',
  },
  {
    value: SimulatedAgeSignalScenario.SupervisedTeen,
    label: 'Supervised teen',
  },
  {
    value: SimulatedAgeSignalScenario.SupervisedApprovalPending,
    label: 'Supervised approval pending',
  },
  {
    value: SimulatedAgeSignalScenario.SupervisedApprovalDenied,
    label: 'Supervised approval denied',
  },
  {
    value: SimulatedAgeSignalScenario.Unknown,
    label: 'Unknown status',
  },
  {
    value: SimulatedAgeSignalScenario.DeclinedSharing,
    label: 'Declined sharing',
  },
  {
    value: SimulatedAgeSignalScenario.NoSignal,
    label: 'No signal',
  },
];

export const createSimulatedAgeSignal = (
  scenario: SimulatedAgeSignalScenario,
): CheckAgeSignalsResult => {
  switch (scenario) {
    case SimulatedAgeSignalScenario.VerifiedAdult:
      return {
        userStatus: UserStatus.Verified,
        ageLower: 18,
      };
    case SimulatedAgeSignalScenario.DeclaredTeen:
      return {
        userStatus: UserStatus.Declared,
        ageLower: 13,
        ageUpper: 15,
      };
    case SimulatedAgeSignalScenario.SupervisedTeen:
      return {
        userStatus: UserStatus.Supervised,
        ageLower: 13,
        ageUpper: 15,
        installId: 'simulated_install_id',
      };
    case SimulatedAgeSignalScenario.SupervisedApprovalPending:
      return {
        userStatus: UserStatus.SupervisedApprovalPending,
        ageLower: 13,
        ageUpper: 15,
        installId: 'simulated_install_id',
        mostRecentApprovalDate: '2026-01-01',
      };
    case SimulatedAgeSignalScenario.SupervisedApprovalDenied:
      return {
        userStatus: UserStatus.SupervisedApprovalDenied,
        ageLower: 13,
        ageUpper: 15,
        installId: 'simulated_install_id',
        mostRecentApprovalDate: '2026-01-01',
      };
    case SimulatedAgeSignalScenario.Unknown:
      return {
        userStatus: UserStatus.Unknown,
      };
    case SimulatedAgeSignalScenario.DeclinedSharing:
      return {
        userStatus: UserStatus.Unknown,
        sharingDeclined: true,
      };
    case SimulatedAgeSignalScenario.NoSignal:
      return {
        userStatus: UserStatus.Empty,
      };
    default:
      return {
        userStatus: UserStatus.Unknown,
      };
  }
};

export const getAgeSignalDecision = (
  appName: string,
  result?: CheckAgeSignalsResult | null,
): AgeSignalDecision => {
  if (!result) {
    return {
      allowed: true,
    };
  }

  switch (result.userStatus) {
    case UserStatus.SupervisedApprovalPending:
      return {
        allowed: false,
        message: getSignificantUpdateApprovalPendingMessage(appName),
      };
    case UserStatus.SupervisedApprovalDenied:
      return {
        allowed: false,
        message: getSignificantUpdateApprovalDeniedMessage(appName),
      };
    default:
      return {
        allowed: true,
      };
  }
};
